import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Ban } from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { cancelledJobPoNotice, type CancelledJobPo } from "@/lib/cancelled-job-pos";

interface CancelJobDialogProps {
    open: boolean;
    onClose: () => void;
    jobId: string;
    /** Shown in the dialog body for context (e.g. "Job #1042"). */
    jobLabel?: string | null;
    /** Called after a successful cancel so the caller can navigate/refresh. */
    onCancelled?: () => void;
}

/**
 * Reusable confirmation dialog for cancelling a job. The cancellation reason is
 * required for everyone except Admins (optional there) — mirrors the server
 * rule in POST /api/jobs/:id/cancel.
 */
export function CancelJobDialog({ open, onClose, jobId, jobLabel, onCancelled }: CancelJobDialogProps) {
    const { isAdmin } = useAuth();
    const qc = useQueryClient();
    const navigate = useNavigate();
    const [reason, setReason] = useState("");

    const cancelJob = useMutation({
        mutationFn: () =>
            api.post<{ open_purchase_orders?: CancelledJobPo[] }>(`/api/jobs/${jobId}/cancel`, {
                reason: reason.trim() || undefined,
            }),
        onSuccess: (data) => {
            toast.success("Job cancelled");
            // #1684: a cancelled job's orders don't stop by themselves.
            const poNotice = cancelledJobPoNotice(data?.open_purchase_orders);
            if (poNotice) {
                toast.warning(poNotice.message, {
                    duration: 20_000,
                    action: { label: "Open PO", onClick: () => navigate(`/purchasing?po=${poNotice.poId}`) },
                });
                qc.invalidateQueries({ queryKey: ["purchase-orders"] });
            }
            qc.invalidateQueries({ queryKey: ["jobs"] });
            qc.invalidateQueries({ queryKey: ["job", jobId] });
            qc.invalidateQueries({ queryKey: ["dispatch"] });
            qc.invalidateQueries({ queryKey: ["office-review-queue"] });
            qc.invalidateQueries({ queryKey: ["office-review-detail", jobId] });
            setReason("");
            onCancelled?.();
            onClose();
        },
        onError: (err: any) => {
            const msg =
                err?.body?.error === "INVOICE_SENT"
                    ? "This job has an invoice that has already been issued. Open the invoice and use Void Invoice first, then cancel the job."
                    : (err?.body?.message ?? err?.message ?? "unknown error");
            toast.error(`Cancel failed: ${msg}`);
        },
    });

    const reasonRequired = !isAdmin;
    const canSubmit = (!reasonRequired || reason.trim().length > 0) && !cancelJob.isPending;

    return (
        <Dialog open={open} onClose={onClose} title="Cancel Job">
            <div className="space-y-4">
                <p className="text-sm text-foreground/80">
                    Cancel {jobLabel ? <span className="font-mono font-medium">{jobLabel}</span> : "this job"}? This
                    clears its assignment and schedule. You can reopen it later from this page (#937), but the
                    appointment won't come back with it.
                </p>
                <div className="space-y-1.5">
                    <Label htmlFor="cancel-reason">Reason{reasonRequired ? " *" : " (optional)"}</Label>
                    <Textarea
                        id="cancel-reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder={
                            reasonRequired
                                ? "Why is this job being cancelled?"
                                : "Optional note about why this job is being cancelled"
                        }
                        rows={3}
                    />
                </div>
                <div className="flex justify-end gap-2 pt-1">
                    <Button variant="outline" onClick={onClose} disabled={cancelJob.isPending}>
                        Keep Job
                    </Button>
                    <Button
                        className="bg-red-600 hover:bg-red-700 text-white"
                        disabled={!canSubmit}
                        onClick={() => cancelJob.mutate()}
                    >
                        {cancelJob.isPending ? (
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                            <Ban className="h-4 w-4 mr-1" />
                        )}
                        Cancel Job
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}
